import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/roles.utils";
import {
  buscarEnMenu,
  segmentosResaltados,
  sinonimoCoincidente,
  tokensDeBusqueda,
} from "../utils/menuSearch";
import multiavatar from "@multiavatar/multiavatar";
import {
  DashboardOutlined,
  CalendarOutlined,
  HomeOutlined,
  TeamOutlined,
  ClearOutlined,
  SkinOutlined,
  InboxOutlined,
  CheckSquareOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  ProfileOutlined,
  KeyOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  BankOutlined,
  HistoryOutlined,
  BarChartOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  UserOutlined,
  IdcardOutlined,
  ContactsOutlined,
  ToolOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ApiOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  CloseCircleFilled,
  SearchOutlined,
  AuditOutlined,
  HddOutlined,
} from "@ant-design/icons";
import "./SideBar.css";

interface SideBarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

type Role = "admin" | "superadmin" | "cliente" | "aseador";

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  roles: Role[];
  /**
   * Sinónimos para el buscador del menú (round 2026-10-28). Solo ayudan a ENCONTRAR el ítem que
   * el rol ya ve: no dan acceso a nada y no inventan rutas. Sin tildes da igual (se normalizan).
   */
  keywords?: string[];
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

/** Un ítem visible para el rol, con el grupo al que pertenece (lo que busca el buscador). */
interface ItemVisible extends MenuItem {
  group: string;
}

const SECTIONS: MenuSection[] = [
  {
    label: "Operaciones",
    items: [
      { title: "Inicio", path: "/home", icon: <DashboardOutlined />, roles: ["admin"], keywords: ["dashboard", "panel", "resumen", "home"] },
      { title: "Calendario", path: "/calendario", icon: <CalendarOutlined />, roles: ["admin", "cliente"], keywords: ["reservas", "arriendos", "bloqueos", "disponibilidad"] },
      // Aseador: vista mensual SOLO de sus aseos. Complementa la tabla de
      // /aseos (esta es la planificación visual, esa es la operativa).
      { title: "Calendario", path: "/calendario-aseador", icon: <CalendarOutlined />, roles: ["aseador"], keywords: ["mis aseos", "agenda", "planificacion"] },
      { title: "Aseos", path: "/aseos", icon: <ClearOutlined />, roles: ["admin", "aseador"], keywords: ["limpieza", "limpiezas", "aseadoras", "agenda de aseo"] },
      { title: "Lavandería", path: "/lavanderia", icon: <SkinOutlined />, roles: ["admin"], keywords: ["lavado", "lavados", "ropa", "sabanas"] },
      { title: "Tareas", path: "/tareas", icon: <CheckSquareOutlined />, roles: ["admin"], keywords: ["mantenciones", "mantencion", "pendientes", "compras"] },
      { title: "Tareas", path: "/tareas-cliente", icon: <CheckSquareOutlined />, roles: ["cliente"], keywords: ["mantenciones", "mantencion", "pendientes"] },
      // Aseador: ve tareas asignadas a él + tareas de propiedades donde
      // tiene aseo hoy o a futuro (filtrado en backend). Ver
      // docs/rol-aseador.md §5bis "Tareas".
      { title: "Tareas", path: "/tareas-aseador", icon: <CheckSquareOutlined />, roles: ["aseador"], keywords: ["mantenciones", "pendientes"] },
    ],
  },
  {
    label: "Gestión",
    items: [
      { title: "Propiedades", path: "/propiedades", icon: <HomeOutlined />, roles: ["admin"], keywords: ["casas", "departamentos", "deptos", "alojamientos", "informacion para el huesped"] },
      { title: "Clientes y propietarios", path: "/clientes-propietarios", icon: <TeamOutlined />, roles: ["admin"], keywords: ["duenos", "dueños", "usuarios", "datos de pago"] },
      // Trabajadores — empleados de Aloja Fácil sin login (mantención,
      // supervisor, contador externo). Round 2026-04-30 — antes se
      // gestionaban implícitamente como `rol="otro"` desde el modal de
      // /sueldos, pero el backend los normalizaba a "cliente" rompiendo
      // el filtro. Ahora tienen su propio CRUD y rol dedicado.
      { title: "Trabajadores", path: "/trabajadores", icon: <IdcardOutlined />, roles: ["admin"], keywords: ["empleados", "personal", "supervisor", "contador"] },
      // Aseadores — CRUD del equipo de aseo (alta/baja, sectores, datos de
      // pago). Round 2026-07-06: se separó de /aseos (que antes tenía un
      // sub-tab "Gestión de aseadores"). /aseos queda solo con la agenda de
      // aseos en Operaciones; la gestión del equipo vive acá, junto a
      // Clientes y Trabajadores.
      { title: "Aseadores", path: "/aseadores", icon: <UserOutlined />, roles: ["admin"], keywords: ["aseadoras", "equipo de aseo", "sectores", "limpieza"] },
      // Agenda de contactos (antes "Agenda de maestros", renombrada
      // 2026-07-02) — directorio de oficios/apoyo (gasfíter, electricista,
      // cerrajero, etc.) con datos de contacto + adjuntos. La URL vieja
      // /agenda-maestros redirige acá; el código (carpeta, modelo, tabla)
      // conserva el identificador técnico agenda-maestros.
      // Costos sugeridos — catálogo de referencia de cuánto sale cada
      // trabajo/reparación, con fotos de guía. Round 2026-06-30.
      { title: "Agenda de contactos", path: "/agenda-contactos", icon: <ContactsOutlined />, roles: ["admin"], keywords: ["maestros", "agenda de maestros", "gasfiter", "electricista", "cerrajero", "oficios", "proveedores"] },
      { title: "Costos sugeridos", path: "/costos-sugeridos", icon: <ToolOutlined />, roles: ["admin"], keywords: ["precios referenciales", "reparaciones", "trabajos", "presupuesto"] },
      // Mercado — datos del scraper de Airbnb (SCRAP 2.0, corre local y
      // empuja vía /api/mercado/ingest): demanda y precios de la competencia
      // por fecha. Admin ve el análisis completo en /mercado; el cliente ve
      // el resumen simplificado en /mercado-cliente ("Precios y demanda").
      // Round 2026-07-21 — ver docs/modulo-mercado.md.
      { title: "Mercado", path: "/mercado", icon: <AreaChartOutlined />, roles: ["admin"], keywords: ["scraper", "airbnb", "competencia", "demanda", "precios", "ocupacion"] },
      { title: "Precios y demanda", path: "/mercado-cliente", icon: <AreaChartOutlined />, roles: ["cliente"], keywords: ["mercado", "competencia", "ocupacion", "tarifas"] },
      { title: "Inventario", path: "/inventario", icon: <InboxOutlined />, roles: ["admin"], keywords: ["bodega", "bodegas", "stock", "productos", "articulos", "compras", "movimientos"] },
      { title: "Documentos", path: "/documentos", icon: <FileTextOutlined />, roles: ["admin", "cliente"], keywords: ["contratos", "archivos", "pdf"] },
      // Round 2026-04-30 nonies — CRUDs dedicados. Antes vivían como
      // pestañas dentro de /finanzas-empresa (que mezclaba reporte +
      // CRUD). Ahora /finanzas-empresa es puro reporte y cada tipo de
      // movimiento tiene su módulo propio bajo Gestión.
      { title: "Arriendos", path: "/arriendos", icon: <KeyOutlined />, roles: ["admin"], keywords: ["reservas", "estadias", "huespedes"] },
      // Round 2026-05-13 — /gastos renombrado a /flujos. El módulo cubre
      // gastos + ingresos + intermediarios (todos los movimientos de caja
      // AF Empresa). El path /gastos sigue siendo un alias funcional para
      // bookmarks; el sidebar apunta al nombre nuevo.
      { title: "Flujos", path: "/flujos", icon: <ShoppingCartOutlined />, roles: ["admin"], keywords: ["gastos", "ingresos", "intermediarios", "caja", "egresos"] },
      { title: "Sueldos", path: "/sueldos", icon: <ProfileOutlined />, roles: ["admin"], keywords: ["pagos", "remuneraciones", "salarios"] },
    ],
  },
  {
    label: "Finanzas",
    items: [
      // Admin: vista para examinar el panel que ve cada aseador y un global
      // con todos. Antes la entrada se llamaba "Finanzas" → /finanzas y
      // mostraba aseos del propio admin (id JWT) en lugar de un aseador
      // seleccionable, lo que se veía siempre vacío. Renombrada y movida a
      // path nuevo en Round 2026-04-29 (ver docs/modulo-finanzas-aseador.md).
      { title: "Finanzas del aseador", path: "/finanzas-aseador-admin", icon: <DollarOutlined />, roles: ["admin"], keywords: ["sueldo aseadora", "pagos aseadores", "liquidacion"] },
      // Ledger del trabajador (rol sin login): reembolsos de inventario que
      // adelantó + movimientos manuales, con ciclo de aprobación. Espejo
      // admin-only del panel del aseador (round 2026-06-20).
      { title: "Finanzas del trabajador", path: "/finanzas-trabajador-admin", icon: <DollarOutlined />, roles: ["admin"], keywords: ["sueldo trabajador", "reembolsos", "pagos trabajadores"] },
      { title: "Finanzas clientes", path: "/finanzas-cliente", icon: <CreditCardOutlined />, roles: ["admin"], keywords: ["liquidaciones", "propietarios", "cierre mensual", "cierre"] },
      { title: "Finanzas", path: "/finanzas-cliente", icon: <CreditCardOutlined />, roles: ["cliente"], keywords: ["liquidaciones", "cierre mensual", "ingresos", "utilidad"] },
      // Aseador: ve sólo su sueldo (aseos completados/pagados, bonos,
      // descuentos). No ve precios ni finanzas de la empresa.
      { title: "Mis ingresos", path: "/finanzas-aseador", icon: <CreditCardOutlined />, roles: ["aseador"], keywords: ["sueldo", "pagos", "dinero", "bonos", "descuentos"] },
      { title: "Finanzas empresa", path: "/finanzas-empresa", icon: <BankOutlined />, roles: ["admin"], keywords: ["utilidad", "ganancias", "resultados", "iva"] },
      { title: "Estadísticas", path: "/estadisticas", icon: <BarChartOutlined />, roles: ["cliente"], keywords: ["ocupacion", "ingresos", "graficos"] },
      { title: "Estadísticas", path: "/estadisticas-admin", icon: <LineChartOutlined />, roles: ["admin"], keywords: ["ocupacion", "ingresos", "graficos", "kpi"] },
    ],
  },
  {
    label: "Sistema",
    items: [
      { title: "Historial", path: "/historial", icon: <HistoryOutlined />, roles: ["admin"], keywords: ["cambios", "movimientos", "deshacer", "auditoria"] },
      { title: "Actividad de clientes", path: "/actividad-clientes", icon: <AuditOutlined />, roles: ["admin"], keywords: ["logins", "navegacion", "accesos", "sesiones"] },
      { title: "Perfil", path: "/perfil", icon: <UserOutlined />, roles: ["admin", "cliente", "aseador"], keywords: ["mi cuenta", "contrasena", "clave", "avatar", "foto", "datos bancarios"] },
      { title: "Configuración", path: "/configuracion", icon: <SettingOutlined />, roles: ["superadmin", "admin"], keywords: ["ajustes", "opciones", "administradores", "iva", "videos", "imagenes", "optimizacion", "umbral de precios", "zonas", "correos", "diagnostico sql"] },
      { title: "Alertas", path: "/alertas", icon: <BellOutlined />, roles: ["superadmin", "admin"], keywords: ["notificaciones", "avisos", "campana", "errores"] },
      { title: "Webhooks Stays", path: "/stays-webhooks", icon: <ApiOutlined />, roles: ["superadmin", "admin"], keywords: ["stays", "webhook", "integracion", "eventos", "sincronizacion"] },
      { title: "Almacenamiento", path: "/almacenamiento", icon: <HddOutlined />, roles: ["admin"], keywords: ["archivos", "fotos", "videos", "espacio", "volumen", "huerfanos", "consumo", "disco"] },
      // /backup exige superadministrador en la ruta (App.tsx) y en el backend: el ítem solo lo ve él.
      // Con roles ["admin"] un administrador común lo veía y lo rebotaban a /home (round 2026-10-27).
      { title: "Backup BD", path: "/backup", icon: <DatabaseOutlined />, roles: ["superadmin"], keywords: ["respaldo", "copia de seguridad", "restaurar", "base de datos", "exportar"] },
    ],
  },
];

// Mapa de rol escalar → etiqueta visible bajo el nombre del usuario.
// Solo hay 4 roles que pueden iniciar sesión: superadmin, admin, aseador y
// cliente. El rol `propietario` ya no existe como login (ver CLAUDE.md §3.3);
// un cliente puede ser a la vez propietario de propiedades, pero siempre se
// etiqueta como "Cliente" en la UI.
const ROLE_LABEL: Record<string, string> = {
  superadministrador: "Superadmin",
  administrador: "Administrador",
  aseador: "Aseador",
  cliente: "Cliente",
};

const pickRoleLabel = (rol: string): string => {
  if (!rol) return "Usuario";
  return ROLE_LABEL[rol] ?? rol;
};

/** ¿El evento viene de un campo donde la persona está escribiendo? (para no robarle la «/»). */
const escribiendoEnCampo = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return true;
  const rol = el.getAttribute("role");
  return rol === "textbox" || rol === "combobox" || rol === "searchbox";
};

/** ¿Hay un modal abierto fuera del menú? Con uno abierto los atajos no se meten en medio. */
const hayDialogoAbierto = (menu: HTMLElement | null): boolean =>
  Array.from(document.querySelectorAll('[role="dialog"]')).some(
    (d) => !(menu && menu.contains(d)) && d.getClientRects().length > 0,
  );

const SideBar: React.FC<SideBarProps> = ({ isOpen, toggleSidebar }) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // El atajo con el menú cerrado lo abre; el foco se pone cuando ya dejó de estar `inert`.
  const enfocarAlAbrir = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const idLista = useId();

  // Texto del buscador. A propósito NO se persiste: se limpia al navegar y al cerrar el menú.
  const [consulta, setConsulta] = useState("");
  const [seleccion, setSeleccion] = useState(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        isOpen &&
        !(event.target as Element).closest(".sidebar-toggle")
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside as EventListener);

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside as EventListener);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, toggleSidebar]);

  // Round 2026-07-27 — antes esto borraba `user` de los storages a mano y
  // navegaba. Era el más incompleto de los dos botones de "Cerrar sesión" de
  // la app: no pegaba al backend (la cookie HttpOnly `authToken` sobrevivía,
  // así que la sesión seguía viva 24 h), no limpiaba el estado en memoria del
  // AuthContext, no avisaba a las otras pestañas y no barría los caches de
  // listas. Ahora delega en el `logout()` del context, que hace las cuatro.
  const handleLogout = () => {
    logout();
    navigate("/login");
    toggleSidebar();
  };

  // `user.rol` es string escalar desde refactor 2026-04 (ver CLAUDE.md §3.3).
  // `normalizeRole` acepta string|string[]|null|legacy y devuelve el rol
  // canónico. NO hacer `user.rol?.[0]` ni `Array.isArray(...) ? [] : ...`
  // — ver el WARNING en roles.utils.ts.
  const rol = normalizeRole(user?.rol);
  const isSuperAdmin = rol === "superadministrador";
  const isAdmin = rol === "administrador" || isSuperAdmin;
  const isCliente = rol === "cliente";
  const isAseador = rol === "aseador";

  const hasAccess = (itemRoles: Role[]): boolean => {
    if (isSuperAdmin && itemRoles.includes("superadmin")) return true;
    if (isAdmin && itemRoles.includes("admin")) return true;
    if (isCliente && itemRoles.includes("cliente")) return true;
    if (isAseador && itemRoles.includes("aseador")) return true;
    return false;
  };

  const visibleSections = SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasAccess(item.roles)),
    }))
    .filter((section) => section.items.length > 0);

  const isActive = (path: string): boolean => {
    if (path === "/home") return location.pathname === "/home" || location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const roleLabel = pickRoleLabel(rol);

  // ── Buscador del menú (round 2026-10-28) ──
  // La lista sale de `visibleSections` —la MISMA que dibuja el menú normal—, nunca de SECTIONS:
  // así el buscador no puede mostrar un ítem que el rol no ve.
  const itemsVisibles: ItemVisible[] = visibleSections.flatMap((s) =>
    s.items.map((item) => ({ ...item, group: s.label })),
  );
  const tokens = tokensDeBusqueda(consulta);
  const buscando = tokens.length > 0;
  // `itemsVisibles` se arma en cada render; la clave estable es el rol + el texto.
  const resultados = useMemo(
    () => buscarEnMenu(itemsVisibles, consulta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rol, consulta],
  );
  const seleccionValida = Math.min(seleccion, Math.max(resultados.length - 1, 0));
  const idOpcion = (i: number) => `${idLista}-op-${i}`;

  const limpiarBusqueda = useCallback(() => {
    setConsulta("");
    setSeleccion(0);
  }, []);

  // Navegar (o cerrar el menú) siempre deja el buscador vacío.
  useEffect(() => {
    limpiarBusqueda();
  }, [location.pathname, limpiarBusqueda]);
  useEffect(() => {
    if (!isOpen) limpiarBusqueda();
  }, [isOpen, limpiarBusqueda]);

  // Foco pendiente: el atajo pulsado con el menú cerrado lo abre y recién acá se enfoca.
  useEffect(() => {
    if (isOpen && enfocarAlAbrir.current) {
      enfocarAlAbrir.current = false;
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // Atajos: «/» (si no se está escribiendo en otro campo) y Ctrl/Cmd+K.
  const abiertoRef = useRef(isOpen);
  abiertoRef.current = isOpen;
  const toggleRef = useRef(toggleSidebar);
  toggleRef.current = toggleSidebar;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const esCtrlK =
        (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k";
      const esBarra = e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (!esCtrlK && !esBarra) return;
      if (esBarra && escribiendoEnCampo(e.target)) return;
      if (hayDialogoAbierto(sidebarRef.current)) return;
      e.preventDefault();
      if (abiertoRef.current) {
        inputRef.current?.focus({ preventScroll: true });
        inputRef.current?.select();
      } else {
        enfocarAlAbrir.current = true;
        toggleRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const elegir = (item: ItemVisible) => {
    limpiarBusqueda();
    navigate(item.path);
    toggleSidebar();
  };

  const onKeyDownBuscador = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Escape") {
      // Con texto: limpia y se queda ahí. Vacío: no hace nada (no cierra el menú ni nada más).
      if (consulta) {
        e.preventDefault();
        e.stopPropagation();
        limpiarBusqueda();
      }
      return;
    }
    if (!buscando || resultados.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSeleccion((seleccionValida + 1) % resultados.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSeleccion((seleccionValida - 1 + resultados.length) % resultados.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      elegir(resultados[seleccionValida]);
    }
  };

  // La opción activa siempre queda a la vista dentro de la lista con scroll.
  useEffect(() => {
    if (!buscando) return;
    document.getElementById(idOpcion(seleccionValida))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionValida, buscando, resultados]);

  const mensajeEstado = !buscando
    ? ""
    : resultados.length === 0
      ? "Sin resultados"
      : `${resultados.length} ${resultados.length === 1 ? "resultado" : "resultados"}`;

  return (
    <>
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={toggleSidebar}
        data-testid="sidebar-overlay"
      />

      <aside
        ref={sidebarRef}
        className={`sidebar ${isOpen ? "open" : ""}`}
        // `inert` bloquea foco y lectores de pantalla a la vez. Usar
        // `aria-hidden` por sí solo es inválido si un descendiente (p.ej.
        // el <Link> activo) sigue con foco — el warning de la consola.
        inert={!isOpen}
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="sidebar-brand-mark">AF</span>
            <h2>Aloja Fácil</h2>
          </div>
          <button
            className="close-button"
            onClick={toggleSidebar}
            aria-label="Cerrar menú"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="user-info">
          <div className="avatar">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt="Avatar"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : user?.avatar ? (
              <div
                dangerouslySetInnerHTML={{ __html: multiavatar(user.avatar) }}
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <UserOutlined />
            )}
          </div>
          <div className="user-details">
            <h3>{user?.nombre || "Usuario"}</h3>
            <span className="user-role-chip">{roleLabel}</span>
          </div>
        </div>

        {/* Buscador: queda FIJO sobre el área con scroll, así no se pierde al bajar por el menú.
            El menú lateral no tiene modo «solo íconos»: o está abierto (cajón) o está oculto e
            `inert`, y en ese caso el buscador oculto tampoco recibe foco; los atajos lo abren. */}
        <div className="sidebar-search" role="search" aria-label="Buscar en el menú">
          <div className="sidebar-search-box">
            <SearchOutlined className="sidebar-search-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              className="sidebar-search-input"
              value={consulta}
              onChange={(e) => {
                setConsulta(e.target.value);
                setSeleccion(0);
              }}
              onKeyDown={onKeyDownBuscador}
              placeholder="Buscar en el menú…"
              title="Buscar en el menú (atajo: / o Ctrl+K)"
              aria-label="Buscar en el menú"
              aria-keyshortcuts="/ Control+K"
              aria-autocomplete="list"
              aria-expanded={buscando && resultados.length > 0}
              aria-controls={buscando && resultados.length > 0 ? idLista : undefined}
              aria-activedescendant={
                buscando && resultados.length > 0 ? idOpcion(seleccionValida) : undefined
              }
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="go"
            />
            {consulta ? (
              <button
                type="button"
                className="sidebar-search-clear"
                aria-label="Limpiar búsqueda"
                title="Limpiar (Esc)"
                // No le quita el foco al campo al tocarlo.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  limpiarBusqueda();
                  inputRef.current?.focus({ preventScroll: true });
                }}
              >
                <CloseCircleFilled />
              </button>
            ) : (
              <kbd className="sidebar-search-kbd" aria-hidden="true">
                /
              </kbd>
            )}
          </div>
          <span className="sidebar-search-sr" role="status" aria-live="polite">
            {mensajeEstado}
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Navegación principal">
          {buscando ? (
            resultados.length === 0 ? (
              <p className="sidebar-search-empty" data-testid="menu-sin-resultados">
                Sin resultados
                <span>Prueba con otra palabra, por ejemplo «finanzas» o «aseo».</span>
              </p>
            ) : (
              <ul
                id={idLista}
                role="listbox"
                aria-label="Resultados de la búsqueda"
                className="sidebar-search-results"
              >
                {resultados.map((item, i) => {
                  const active = isActive(item.path);
                  const sinonimo = sinonimoCoincidente(item, tokens);
                  return (
                    <li
                      key={`${item.path}|${item.title}`}
                      id={idOpcion(i)}
                      role="option"
                      aria-selected={i === seleccionValida}
                      className={[active ? "active" : "", i === seleccionValida ? "is-selected" : ""]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseMove={() => i !== seleccionValida && setSeleccion(i)}
                    >
                      <Link
                        to={item.path}
                        tabIndex={-1}
                        onClick={() => {
                          limpiarBusqueda();
                          toggleSidebar();
                        }}
                        className={active ? "active" : ""}
                        aria-current={active ? "page" : undefined}
                      >
                        <span className="menu-icon">{item.icon}</span>
                        <span className="menu-text">
                          <span className="menu-label">
                            {segmentosResaltados(item.title, tokens).map((seg, k) =>
                              seg.resaltado ? (
                                <mark key={k} className="sidebar-search-mark">
                                  {seg.texto}
                                </mark>
                              ) : (
                                <React.Fragment key={k}>{seg.texto}</React.Fragment>
                              ),
                            )}
                          </span>
                          <span className="menu-sub">
                            {item.group}
                            {sinonimo ? ` · ${sinonimo}` : ""}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            visibleSections.map((section) => (
              <div key={section.label} className="sidebar-section">
                <span className="sidebar-section-label">{section.label}</span>
                <ul>
                  {section.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <li key={item.path} className={active ? "active" : ""}>
                        <Link
                          to={item.path}
                          onClick={toggleSidebar}
                          className={active ? "active" : ""}
                          aria-current={active ? "page" : undefined}
                        >
                          <span className="menu-icon">{item.icon}</span>
                          <span className="menu-label">{item.title}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </nav>

        <div className="sidebar-footer">
          <button
            className="logout-button"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
          >
            <LogoutOutlined className="menu-icon" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const SideBarToggle: React.FC<{
  toggleSidebar: () => void;
  isOpen?: boolean;
}> = ({ toggleSidebar, isOpen = false }) => {
  return (
    <button
      className={`sidebar-toggle ${isOpen ? "is-open" : ""}`}
      onClick={toggleSidebar}
      aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
      aria-expanded={isOpen}
    >
      <MenuOutlined className="toggle-icon" />
    </button>
  );
};

export default SideBar;
